import { useEffect } from 'react';

/**
 * Moniteur de performance avancé pour optimiser les Core Web Vitals
 * - Prévention des layout shifts (CLS)
 * - Optimisation du chargement des fonts (FOIT/FOUT)
 * - Détection et logging des problèmes de performance
 */
export function AdvancedPerformanceMonitor() {
  useEffect(() => {
    // 1. Prévenir les layout shifts SANS forced reflow
    const preventImageLayoutShifts = () => {
      const images = document.querySelectorAll('img:not([width]):not([height])');
      
      images.forEach((img) => {
        const htmlImg = img as HTMLImageElement;
        
        // Attendre que l'image soit chargée AVANT de lire les dimensions
        // Cela évite les forced reflows en batch les lectures géométriques
        if (htmlImg.complete && htmlImg.naturalWidth > 0 && htmlImg.naturalHeight > 0) {
          // Utiliser requestAnimationFrame pour batch les modifications DOM
          requestAnimationFrame(() => {
            htmlImg.setAttribute('width', String(htmlImg.naturalWidth));
            htmlImg.setAttribute('height', String(htmlImg.naturalHeight));
            htmlImg.style.aspectRatio = `${htmlImg.naturalWidth} / ${htmlImg.naturalHeight}`;
          });
        } else {
          // Pour les images non chargées, attendre l'événement load
          htmlImg.addEventListener('load', function onImageLoad() {
            if (htmlImg.naturalWidth > 0 && htmlImg.naturalHeight > 0) {
              requestAnimationFrame(() => {
                htmlImg.setAttribute('width', String(htmlImg.naturalWidth));
                htmlImg.setAttribute('height', String(htmlImg.naturalHeight));
                htmlImg.style.aspectRatio = `${htmlImg.naturalWidth} / ${htmlImg.naturalHeight}`;
              });
            }
            htmlImg.removeEventListener('load', onImageLoad);
          }, { once: true });
        }
      });
    };

    // 2. Optimiser le chargement des fonts pour éviter FOIT (Flash of Invisible Text)
    const optimizeFontLoading = () => {
      if ('fonts' in document) {
        // Précharger les fonts critiques
        const criticalFonts = [
          { family: 'Orbitron', weight: '400' },
          { family: 'Orbitron', weight: '700' },
          { family: 'Press Start 2P', weight: '400' }
        ];

        criticalFonts.forEach(({ family, weight }) => {
          (document as any).fonts.load(`${weight} 1em ${family}`).catch(() => {
            console.warn(`Failed to preload font: ${family} ${weight}`);
          });
        });
      }
    };

    // 3. Optimiser les événements de scroll avec passive listeners
    const optimizeScrollEvents = () => {
      const scrollHandler = () => {
        // Utiliser requestAnimationFrame pour les opérations visuelles
        requestAnimationFrame(() => {
          // Opérations de scroll optimisées
        });
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
      return () => window.removeEventListener('scroll', scrollHandler);
    };

    // 4. Détecter et logger les layout shifts via PerformanceObserver
    const observeLayoutShifts = () => {
      if ('PerformanceObserver' in window) {
        try {
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
                const layoutShift = entry as any;
                if (layoutShift.value > 0.1) {
                  console.warn('Large layout shift detected:', {
                    value: layoutShift.value,
                    sources: layoutShift.sources?.map((source: any) => ({
                      node: source.node,
                      previousRect: source.previousRect,
                      currentRect: source.currentRect
                    }))
                  });
                }
              }
            }
          });

          observer.observe({ entryTypes: ['layout-shift'] });
          return () => observer.disconnect();
        } catch (e) {
          console.warn('PerformanceObserver not fully supported:', e);
        }
      }
    };

    // 5. Observer le LCP (Largest Contentful Paint)
    const observeLCP = () => {
      if ('PerformanceObserver' in window) {
        try {
          const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1] as any;
            
            if (lastEntry && lastEntry.renderTime > 2500) {
              console.warn('Slow LCP detected:', {
                renderTime: lastEntry.renderTime,
                element: lastEntry.element,
                url: lastEntry.url
              });
            }
          });

          observer.observe({ entryTypes: ['largest-contentful-paint'] });
          return () => observer.disconnect();
        } catch (e) {
          console.warn('LCP observer not supported:', e);
        }
      }
    };

    // Initialiser tous les moniteurs
    preventImageLayoutShifts();
    optimizeFontLoading();
    const cleanupScroll = optimizeScrollEvents();
    const cleanupLayoutShifts = observeLayoutShifts();
    const cleanupLCP = observeLCP();

    // Observer les mutations DOM pour appliquer les optimisations aux nouveaux éléments
    const mutationObserver = new MutationObserver(() => {
      preventImageLayoutShifts();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      cleanupScroll?.();
      cleanupLayoutShifts?.();
      cleanupLCP?.();
      mutationObserver.disconnect();
    };
  }, []);

  return null; // Composant sans UI, uniquement pour les side effects
}
